<script lang="ts">
  import { api } from '$shared/api';
  import { gameLabel } from '$lib/utils/challenge';
  import Avatar from '$shared/Avatar.svelte';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';

  let { token }: { token: string } = $props();

  interface SeriesMatch {
    id: number;
    public_token: string;
    game_key: string;
    status: 'pending' | 'completed' | 'expired';
    winner_id: number | null;
    challenger_score: number | null;
    opponent_score: number | null;
    created_at: string;
  }

  interface SeriesView {
    id: number;
    public_token: string;
    challenger_id: number;
    opponent_id: number;
    game_key: string;
    format: number;
    challenger_wins: number;
    opponent_wins: number;
    winner_id: number | null;
    status: 'pending' | 'completed' | 'expired';
    created_at: string;
    completed_at: string | null;
    challenger_username: string;
    challenger_avatar_url: string | null;
    opponent_username: string;
    opponent_avatar_url: string | null;
    target_wins: number;
    next_to_play_id: number | null;
    matches: SeriesMatch[];
  }

  let series = $state<SeriesView | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let copied = $state(false);

  $effect(() => {
    loading = true;
    error = null;
    api
      .get<SeriesView>(`/series/${token}`)
      .then((d) => {
        series = d;
        loading = false;
      })
      .catch((e: unknown) => {
        error = e instanceof Error ? e.message : 'Series not found';
        loading = false;
      });
  });

  const gameLabelDerived = $derived(series ? gameLabel(series.game_key) : '');

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(location.href);
      copied = true;
      setTimeout(() => {
        copied = false;
      }, 2000);
    } catch {
      console.error('Failed to copy link');
    }
  }
</script>

<div class="series-wrapper">
  {#if loading}
    <LoadingSkeleton lines={4} />
  {:else if error || !series}
    <div class="error-card">
      <h1>Series unavailable</h1>
      <p>{error ?? 'Series not found.'}</p>
    </div>
  {:else}
    <div class="series-card">
      <h1>Best of {series.format} — {gameLabelDerived}</h1>

      <div class="players">
        <div class="player">
          <Avatar username={series.challenger_username} avatarUrl={series.challenger_avatar_url} size="lg" />
          <strong>@{series.challenger_username}</strong>
          <div class="score">{series.challenger_wins}</div>
          {#if series.winner_id === series.challenger_id}
            <span class="pill pill--winner">Series Winner</span>
          {/if}
        </div>
        <div class="vs">vs</div>
        <div class="player">
          <Avatar username={series.opponent_username} avatarUrl={series.opponent_avatar_url} size="lg" />
          <strong>@{series.opponent_username}</strong>
          <div class="score">{series.opponent_wins}</div>
          {#if series.winner_id === series.opponent_id}
            <span class="pill pill--winner">Series Winner</span>
          {/if}
        </div>
      </div>

      <p class="target">First to <strong>{series.target_wins}</strong> wins the series.</p>

      <div class="matches">
        <h2>Matches</h2>
        {#each series.matches as m, i}
          <div class="match" class:match--completed={m.status === 'completed'}>
            <span class="match-num">Match {i + 1}</span>
            <span class="match-status">{m.status}</span>
            {#if m.status === 'completed' && m.winner_id}
              <span class="match-winner">
                Winner: {m.winner_id === series.challenger_id ? series.challenger_username : series.opponent_username}
              </span>
            {/if}
            {#if m.status !== 'pending'}
              <a href={`#/challenge/${m.public_token}`} class="match-link">View →</a>
            {/if}
          </div>
        {/each}
      </div>

      {#if series.status === 'pending' && series.next_to_play_id}
        <p class="waiting">
          Waiting on @{series.next_to_play_id === series.challenger_id
            ? series.challenger_username
            : series.opponent_username} to play next.
        </p>
      {/if}

      <div class="info-section">
        <p class="info-text">Open in the Beat Duel app — this series is played there.</p>
        <p class="info-muted">
          Don't have the app yet? Beat Duel for iPhone is in beta — ask your friend for a TestFlight invite.
        </p>
      </div>
      <button type="button" class="btn btn-secondary" onclick={handleCopy}>
        {copied ? 'Copied!' : 'Copy link'}
      </button>
    </div>
  {/if}
</div>

<style>
  .series-wrapper {
    display: flex;
    justify-content: center;
    padding-top: 2rem;
  }
  .series-card,
  .error-card {
    max-width: 520px;
    width: 100%;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  h1 {
    margin: 0;
    font-size: 22px;
    text-align: center;
  }
  h2 {
    font-size: 14px;
    margin: 0 0 8px;
    color: var(--text-dim);
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .players {
    display: flex;
    align-items: center;
    gap: 16px;
    justify-content: center;
  }
  .player {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    flex: 1;
  }
  .vs {
    color: var(--text-dim);
    font-weight: 700;
    letter-spacing: 0.06em;
  }
  .score {
    font-size: 2.2rem;
    font-weight: 800;
    color: var(--pink);
  }
  .target {
    text-align: center;
    color: var(--text-dim);
    font-size: 14px;
    margin: 0;
  }
  .pill {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
  }
  .pill--winner {
    background: rgba(34, 197, 94, 0.18);
    color: var(--green);
  }
  .matches {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .match {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    background: var(--surface-2);
    border-radius: 8px;
    font-size: 13px;
  }
  .match--completed {
    opacity: 0.85;
  }
  .match-num {
    font-weight: 600;
  }
  .match-status {
    color: var(--text-dim);
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .match-winner {
    flex: 1;
    color: var(--green);
    font-size: 12px;
  }
  .match-link {
    color: var(--pink);
    text-decoration: none;
    font-size: 12px;
  }
  .waiting {
    text-align: center;
    color: var(--text-dim);
    font-size: 14px;
    margin: 0;
  }
  .info-section {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid var(--border);
  }
  .info-text {
    font-size: 0.9375rem;
    color: var(--text);
    margin: 0;
    line-height: 1.5;
  }
  .info-muted {
    font-size: 0.875rem;
    color: var(--text-muted);
    margin: 0;
    line-height: 1.4;
  }
  .btn-secondary {
    background: var(--bg);
    border: 1px solid var(--border);
    color: var(--text);
    padding: 0.625rem;
    font-size: 0.9375rem;
    border-radius: 8px;
    cursor: pointer;
  }
  .btn-secondary:hover {
    background: var(--surface-hover);
  }
</style>
